// src/App.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { supabase, BACKEND_URL } from './supabaseClient'; 
import { toast, Toaster } from 'react-hot-toast'; 

// --- NEW CSS IMPORTS (Global Styles) ---
import './experiment_css/tailwind-build.css'; 
import './experiment_css/index.css'; 
import './App.css'; 

// --- Core Components ---
import Header from './components/Header';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import ResultModal from './components/ResultModal'; 

// --- Page Imports ---
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage'; 
import UpdatePasswordPage from './pages/UpdatePasswordPage';
import HomePage from './pages/HomePage';
import PricingPage from './pages/PricingPage'; 
import ApparelPage from './pages/ApparelPage';
import MugPage from './pages/MugPage';
import SocialPage from './pages/SocialPage';
import BlogPage from './pages/BlogPage';
import FaqPage from './pages/FaqPage';
import BlogPostPage from './pages/BlogPostPage'; 
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import RefundPage from './pages/RefundPage';
import RemoveBgPage from './pages/RemoveBgPage'; 
import BlurBgPage from './pages/BlurBgPage'; 
import BgReplacePage from './pages/BgReplacePage';
import ContactUsPage from './pages/ContactUsPage';
import AboutUsPage from './pages/AboutUsPage';
import SitemapPage from './pages/SitemapPage';
import ToolPage from './pages/ToolPage';

// --- NEW LANDING PAGE IMPORTS ---
import ApparelLandingPage from './pages/ApparelLandingPage';
import HomeLivingLandingPage from './pages/HomeLivingLandingPage'; 
import TechSocialLandingPage from './pages/TechSocialLandingPage'; 
import ImageConverterPage from './pages/ImageConverterPage';

// --- ACCOUNT LAYOUT & PAGES ---
import AccountLayout from './pages/account/AccountLayout';
import ProfileSettings from './pages/account/ProfileSettings';
import SecuritySettings from './pages/account/SecuritySettings';
import MockupGallery from './pages/account/MockupGallery';
import UsagePlan from './pages/account/UsagePlan';
import BillingHistory from './pages/account/BillingHistory';

// --- Job Status Panel ---
function JobStatusPanel({ jobs }) {
  if (jobs.length === 0) return null; 
  return (
    <div className="job-status-panel" style={{zIndex: 9999}}>
      <div className="job-status-header">
        <div className="loader-spinner-small"></div>
        <span>Generating... ({jobs.length})</span>
      </div>
      <div className="job-status-body">
        <p>Your mockup(s) are in progress.</p>
      </div>
    </div>
  );
}

function App() {
  const [session, setSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [activeJobs, setActiveJobs] = useState([]); 
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultData, setResultData] = useState(null); 

  // --- NEW: THEME STATE MANAGEMENT ---
  // 1. Initialize state from localStorage or default to 'light'
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');

  // 2. Effect to apply the class to the body tag
  useEffect(() => {
    if (theme === 'dark') {
      document.body.classList.add('tw-dark');
    } else {
      document.body.classList.remove('tw-dark');
    }
    // Save to localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  // 3. The Toggle Function
  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  };
  // -----------------------------------

  const handleJobStarted = (jobId) => {
    console.log(`[App.js] New Job Ticket received: ${jobId}`);
    setActiveJobs(prevJobs => [...prevJobs, jobId]);
  };

  const refreshProfile = async () => {
    if (!session) return null;
    try {
        const response = await fetch(`${BACKEND_URL}/api/profile?t=${new Date().getTime()}`, { 
            headers: { 'Authorization': `Bearer ${session.access_token}`, 'Cache-Control': 'no-cache' }
        });
        if (response.ok) {
            const data = await response.json();
            setProfile(data);
            return data;
        }
    } catch (err) { console.error("Profile refresh error", err); }
    return null;
  };

  useEffect(() => {
    setLoadingSession(true); 
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession); 
      setLoadingSession(false); 
    }).catch(error => {
        console.error("Error getting initial session:", error);
        setLoadingSession(false); 
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sessionUpdate) => {
      if (sessionStorage.getItem('isDownloading') === 'true') return; 
      setSession(sessionUpdate);
      if (!sessionUpdate) {
        setProfile(null);
        setActiveJobs([]); 
      }
    });
    return () => subscription.unsubscribe();
  }, []); 

  useEffect(() => {
    if (session && !loadingSession) refreshProfile();
  }, [session, loadingSession]); 

  // Navigation Guard
  useEffect(() => {
    const currentlyDownloading = sessionStorage.getItem('isDownloading') === 'true';
    if (currentlyDownloading) return;
    
    if (!loadingSession) {
      const currentPath = window.location.pathname;
      const publicPaths = [
        '/', '/login', '/forgot-password', '/update-password', 
          '/pricing', '/blog', '/faq', '/terms', '/privacy', '/refund', 
          '/contact-us', '/about-us', '/sitemap',
          '/apparel', '/home-living', '/tech-social', 
          '/tool', '/tool/background-remove', '/tool/blur-background', '/tool/image-converter', '/tool/background-replacer'
        ];

      if (session && profile) {
        if ((!profile.full_name || profile.full_name.trim() === '')) {
           const allowedPaths = ['/faq', '/blog', '/terms', '/privacy', '/refund', '/contact-us', '/about-us'];
           const isAllowed = allowedPaths.some(path => currentPath.startsWith(path));
           const isAccountPage = currentPath.startsWith('/account');

           if (!isAllowed && !isAccountPage) {
             navigate('/account/profile'); 
           }
        } else {
           if (currentPath === '/login') navigate('/app');
        }
      } else if (!session) {
        const isPublic = publicPaths.some(path => 
          currentPath === path || (path !== '/' && currentPath.startsWith(path))
        );
        if (!isPublic) navigate('/login');
      }
    }
  }, [session, loadingSession, profile, navigate]); 

  // Job Polling
  useEffect(() => {
    if (activeJobs.length === 0 || !session?.access_token) return;
    const pollInterval = setInterval(() => {
      activeJobs.forEach(async (jobId) => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/generate-mockup/status/${jobId}`, {
             headers: { 'Authorization': `Bearer ${session.access_token}` }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.status === 'completed') {
              setActiveJobs(prev => prev.filter(id => id !== jobId));
              const finalUrl = data.resultUrl || data.result_url || data.result?.url;
              const finalId = data.mockupId || data.id || data.result?.id;
              if (finalUrl) {
                  setResultData({ url: finalUrl, id: finalId });
                  setShowResultModal(true);
              } else {
                  toast.error("Generation complete, but image URL is missing.");
              }
            } else if (data.status === 'failed') {
              toast.error("Generation failed. Please try again.");
              setActiveJobs(prev => prev.filter(id => id !== jobId));
            }
          }
        } catch (err) { console.error(err); }
      });
    }, 2000); 
    return () => clearInterval(pollInterval);
  }, [activeJobs, navigate, session]);

  const handleLogout = async () => await supabase.auth.signOut();

  return (
    // Note: 'tw-dark' class is now handled by the useEffect on the body element,
    // but keeping dark: classes here is fine for inner content.
    <div className="App tw-bg-[#fcfcfc] dark:tw-bg-black tw-min-h-screen tw-flex tw-flex-col">
      <Toaster position="bottom-center" containerStyle={{ zIndex: 9999999 }} />
      <JobStatusPanel jobs={activeJobs} />
      
      {showResultModal && resultData && (
          <ResultModal 
              isOpen={showResultModal}
              onClose={() => setShowResultModal(false)}
              imageUrl={resultData.url}
              mockupId={resultData.id}
              session={session}
          />
      )}

      {/* --- NEW HEADER: Passing Theme Props --- */}
      <Header 
        session={session} 
        profile={profile} 
        onLogout={handleLogout} 
        currentTheme={theme}        // NEW
        toggleTheme={toggleTheme}   // NEW
      />
      
      {/* --- MAIN CONTENT (Pushes Footer down) --- */}
      <main className="main-content tw-flex-grow">
        <Routes>
          <Route path="/" element={<LandingPage session={session} />} />
          <Route path="/pricing" element={<PricingPage />} /> 
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/update-password" element={<UpdatePasswordPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/refund" element={<RefundPage />} />
          <Route path="/contact-us" element={<ContactUsPage />} />
          <Route path="/about-us" element={<AboutUsPage />} />
          <Route path="/sitemap" element={<SitemapPage />} /> 
          <Route path="/tool" element={<ToolPage session={session} />} />

          <Route path="/apparel" element={<ApparelLandingPage />} />
          <Route path="/home-living" element={<HomeLivingLandingPage />} />
          <Route path="/tech-social" element={<TechSocialLandingPage />} />

          <Route element={<ProtectedRoute session={session} loading={loadingSession} />}>
            <Route path="/app" element={<HomePage session={session} onJobStarted={handleJobStarted} />} />
            <Route path="/account" element={<AccountLayout profile={profile} />}> 
              <Route index element={<ProfileSettings session={session} />} />
              <Route path="profile" element={<ProfileSettings session={session} />} />
              <Route path="security" element={<SecuritySettings session={session} />} />
              <Route path="usage" element={<UsagePlan />} />
              <Route path="billing" element={<BillingHistory />} />
              <Route path="gallery" element={<MockupGallery session={session} />} />
            </Route>
            <Route path="/app/apparel" element={<ApparelPage session={session} onJobStarted={handleJobStarted} refreshProfile={refreshProfile} />} />
            <Route path="/app/home-living" element={<MugPage session={session} onJobStarted={handleJobStarted} refreshProfile={refreshProfile} />} />
            <Route path="/app/tech-social" element={<SocialPage session={session} onJobStarted={handleJobStarted} refreshProfile={refreshProfile} />} />
            <Route path="/app/tool/background-replacer" element={<BgReplacePage session={session} refreshProfile={refreshProfile} />} />
            <Route path="/app/tool/background-remove" element={<RemoveBgPage session={session} refreshProfile={refreshProfile} />} />
            <Route path="/app/tool/image-converter" element={<ImageConverterPage session={session} refreshProfile={refreshProfile} />} />
            <Route path="/app/tool/blur-background" element={<BlurBgPage session={session} refreshProfile={refreshProfile} />} />
          </Route>
          <Route path="*" element={<LandingPage session={session} />} />
        </Routes>
      </main>

      {/* --- NEW FOOTER --- */}
      <Footer />
    </div>
  );
}

export default App;